import { useEffect, useCallback, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import ApplicationForm from "@/pages/portal/components/ApplicationForm/ApplicationForm"
import { getApplicationDetail } from "@/services/universityService"
import type { Application } from "@/pages/portal/types"
import { Loader2 } from "lucide-react"

export default function AgencyApplicationFormPage() {
    const navigate = useNavigate()
    const { memberId } = useParams<{ memberId: string }>()
    const [searchParams] = useSearchParams()
    const existingAppNumber = searchParams.get("id") || undefined
    const urlStep = (() => {
        const stepParam = searchParams.get("step")
        const parsed = stepParam ? Number(stepParam) : 0
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
    })()

    const [draftTerm, setDraftTerm] = useState<string | undefined>()
    const [draftDegree, setDraftDegree] = useState<string | undefined>()
    const [draftProgram, setDraftProgram] = useState<number | undefined>()
    const [draftPrograms, setDraftPrograms] = useState<number[] | undefined>()
    const [draftApplicationType, setDraftApplicationType] = useState<string | undefined>()
    const [detailLoaded, setDetailLoaded] = useState(!existingAppNumber)

    useEffect(() => {
        if (memberId) {
            sessionStorage.setItem("agency_acting_member_id", memberId)
        }
        return () => {
            sessionStorage.removeItem("agency_acting_member_id")
        }
    }, [memberId])

    useEffect(() => {
        if (!existingAppNumber) return
        getApplicationDetail(existingAppNumber)
            .then((detail) => {
                if (detail.status === "success") {
                    setDraftTerm(detail.term_id ?? undefined)
                    setDraftDegree(detail.degree_type ?? detail.degree_level ?? undefined)
                    if (detail.interested_programs?.length) {
                        setDraftPrograms(detail.interested_programs)
                        setDraftProgram(detail.interested_programs[0])
                    } else if (detail.price_list_entry_id) {
                        setDraftProgram(detail.price_list_entry_id)
                        setDraftPrograms([detail.price_list_entry_id])
                    }
                    if (detail.application_type) setDraftApplicationType(detail.application_type)
                }
            })
            .catch(() => {})
            .finally(() => setDetailLoaded(true))
    }, [existingAppNumber])

    const handleCancel = useCallback(() => {
        navigate(`/partner/students/${memberId}`)
    }, [navigate, memberId])

    const handleSubmit = useCallback((_app: Application) => {
        navigate(`/partner/students/${memberId}`)
    }, [navigate, memberId])

    const handleApplicationCreated = useCallback((appNumber: string) => {
        window.history.replaceState(
            null,
            "",
            `/partner/students/${memberId}/create-application?id=${appNumber}&step=1`
        )
    }, [memberId])

    const handleStepChange = useCallback((appNumber: string, step: number) => {
        window.history.replaceState(
            null,
            "",
            `/partner/students/${memberId}/create-application?id=${appNumber}&step=${step}`
        )
    }, [memberId])

    if (!detailLoaded) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
        )
    }

    return (
        <ApplicationForm
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            onApplicationCreated={handleApplicationCreated}
            onStepChange={handleStepChange}
            applicationNumber={existingAppNumber}
            initialStep={urlStep}
            initialTerm={draftTerm}
            initialDegree={draftDegree}
            initialProgram={draftProgram}
            initialPrograms={draftPrograms}
            initialApplicationType={draftApplicationType}
        />
    )
}
